import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Check, Warning } from '@phosphor-icons/react'

interface ToastItem {
  id: number
  message: string
  type: 'notice' | 'alert'
}

let nextId = 0

const TOAST_DURATION = 4000

export function ToastContainer({ notice, alert }: { notice?: string; alert?: string }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  useEffect(() => {
    const newToasts: ToastItem[] = []
    if (notice) newToasts.push({ id: nextId++, message: notice, type: 'notice' })
    if (alert) newToasts.push({ id: nextId++, message: alert, type: 'alert' })
    if (newToasts.length > 0) setToasts(prev => [...prev, ...newToasts])
  }, [notice, alert])

  const dismiss = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  if (toasts.length === 0) return null

  return createPortal(
    <div className="fixed bottom-lg left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-xs pointer-events-none">
      {toasts.map(toast => (
        <ToastMessage key={toast.id} toast={toast} onDismiss={dismiss} />
      ))}
    </div>,
    document.body
  )
}

function ToastMessage({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setExiting(true), TOAST_DURATION)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (exiting) {
      const timer = setTimeout(() => onDismiss(toast.id), 300)
      return () => clearTimeout(timer)
    }
  }, [exiting, toast.id, onDismiss])

  const isAlert = toast.type === 'alert'
  const Icon = isAlert ? Warning : Check

  return (
    <button
      onClick={() => setExiting(true)}
      className={`pointer-events-auto flex items-center gap-sm px-md py-sm rounded shadow-page font-label text-sm tracking-wide transition-all duration-300 ${
        exiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
      } ${
        isAlert
          ? 'bg-danger/95 text-white'
          : 'bg-spine/95 text-paper'
      }`}
      style={{ animation: 'toast-in 0.3s ease-out' }}
    >
      <Icon weight="bold" size={16} />
      {toast.message}
    </button>
  )
}
